const base = process.env.REACT_APP_SERVERURL || "";

const studioJson = async (url: string, init?: RequestInit) => {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = "Bearer " + token;
  }
  try {
    const res = await fetch(url, {
      ...init,
      headers: {
        ...headers,
        ...(init?.headers as Record<string, string> | undefined),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const backendMessage = String(json?.message || "").trim();
      const isRemove = url.includes("/media/remove");
      let message = backendMessage || "Could not complete that capture request.";
      if (backendMessage === "FEATURE_DISABLED") {
        message = "Field capture is not enabled on the server. Restart the loc backend.";
      }
      if (backendMessage === "Forbidden" || res.status === 403) {
        message = isRemove
          ? "Your login cannot remove process photos or videos."
          : "Your login cannot create capture links. Use an organisation admin or manager account.";
      }
      if (!backendMessage && (res.status === 404 || res.status === 400)) {
        message = isRemove
          ? "Could not remove those files. Open the product from Edit or View and try again."
          : "Could not find this product for capture. Open the product from Edit or View and try again.";
      }
      return { success: false, message, data: json?.data ?? null };
    }
    return json;
  } catch {
    return { success: false, message: "Could not reach the server.", data: null };
  }
};

export type CaptureKind = "PHOTO" | "VIDEO";

export type PublicCaptureSession = {
  status: string;
  productName?: string;
  processTitle?: string;
  instruction?: string;
  expiresAt?: string;
  remainingUploads?: number;
  maxUploads?: number;
  usedCount?: number;
  submitted?: boolean;
  allowedKinds?: CaptureKind[];
  maxVideoSeconds?: number;
  maxPhotoBytes?: number;
  maxVideoBytes?: number;
  message?: string;
};

const publicJson = async (url: string, init?: RequestInit) => {
  try {
    const res = await fetch(url, init);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        message: json?.message || "This capture link was not found.",
        data: json?.data ?? null,
      };
    }
    return json;
  } catch {
    return { success: false, message: "Could not open this capture link right now.", data: null };
  }
};

export const createProductCaptureLink = (body: {
  productId: string;
  processTitle: string;
  processIndex?: number;
  productProcessId?: string;
  instruction?: string;
  allowedKinds?: CaptureKind[];
  expiryHours?: number;
  maxUploads?: number;
  recipientPhone?: string;
}) =>
  studioJson(`${base}/product-capture-links`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const listProductCaptureLinks = (
  productId: string,
  processTitle: string,
  productProcessId?: string
) => {
  const query = new URLSearchParams();
  query.set("productId", productId);
  const id = String(productProcessId || "").trim();
  if (id) {
    query.set("productProcessId", id);
  } else {
    query.set("processTitle", processTitle);
  }
  return studioJson(`${base}/product-capture-links?${query.toString()}`);
};

export const revokeProductCaptureLink = (id: string) =>
  studioJson(`${base}/product-capture-links/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
    body: JSON.stringify({}),
  });

export const removeProductProcessMedia = (body: {
  productId: string;
  processTitle: string;
  processIndex?: number;
  productProcessId?: string;
  mediaUrls: string[];
}) =>
  studioJson(`${base}/product-capture-links/media/remove`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const getPublicCaptureSession = (token: string): Promise<{ success?: boolean; data?: PublicCaptureSession; message?: string }> =>
  publicJson(`${base}/product-capture-links/public/${encodeURIComponent(token)}`);

export const presignPublicCapture = (
  token: string,
  body: { fileType: string; kind: CaptureKind }
) =>
  publicJson(`${base}/product-capture-links/public/${encodeURIComponent(token)}/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const completePublicCapture = (
  token: string,
  body: {
    mediaUrl: string;
    kind: CaptureKind;
    type?: string;
    lat: number;
    lng: number;
    accuracyM?: number;
    capturedAt: number;
    note?: string;
    finalize?: boolean;
  }
) =>
  publicJson(`${base}/product-capture-links/public/${encodeURIComponent(token)}/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const finalizePublicCapture = (token: string) =>
  publicJson(`${base}/product-capture-links/public/${encodeURIComponent(token)}/finalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });

export const putCaptureBlob = (
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (evt) => {
      const total = evt.total || blob.size || 0;
      if (!total || !onProgress) return;
      onProgress(Math.min(99, Math.round((evt.loaded * 100) / total)));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error("Upload failed. Keep this page open and try Send again."));
    };
    xhr.onerror = () => reject(new Error("Network dropped while sending. Keep this page open and try again."));
    xhr.send(blob);
  });

export const mimeToCaptureFileType = (mime: string, kind: CaptureKind): string => {
  const lower = (mime || "").toLowerCase();
  if (kind === "PHOTO") {
    if (lower.includes("png")) return "image/png";
    return "image/jpeg";
  }
  if (lower.includes("mp4")) return "video/mp4";
  if (lower.includes("quicktime") || lower.includes("mov")) return "video/quicktime";
  return "video/webm";
};
